import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'urql';
import PageHeader from '../../../components/PageHeader.jsx';
import StickyActions from '../../../components/StickyActions.jsx';
import DataTable from '../../../components/Table/DataTable.jsx';
import { LIST_EGRESOS, LIST_INGRESOS } from './gql.js';

const columns = [
  { key: 'fecha', label: 'Fecha' },
  { key: 'tipo', label: 'Tipo' },
  { key: 'cliente', label: 'Cliente' },
  { key: 'remito', label: 'Remito' },
  { key: 'usuario', label: 'Usuario' },
  { key: 'cantidadTotal', label: 'Cantidad total' },
];

export default function MovimientosList({ onCreateIngreso, onCreateEgreso, onTransferir, onSelectIngreso, onSelectEgreso, refreshKey = 0 }) {
  const [tab, setTab] = useState('ingresos');
  const [filters, setFilters] = useState({ clienteId: '', search: '', fechaDesde: '', fechaHasta: '' });
  const filter = useMemo(() => ({
    clienteId: filters.clienteId || null,
    search: filters.search || null,
    fechaDesde: filters.fechaDesde || null,
    fechaHasta: filters.fechaHasta || null,
    page: 1,
    pageSize: 50,
  }), [filters]);
  const [{ data: ingresosData, fetching: ingresosLoading, error: ingresosError }, reexecuteIngresos] = useQuery({
    query: LIST_INGRESOS,
    variables: { filter },
  });
  const [{ data: egresosData, fetching: egresosLoading, error: egresosError }, reexecuteEgresos] = useQuery({
    query: LIST_EGRESOS,
    variables: { filter },
  });

  useEffect(() => {
    reexecuteIngresos({ requestPolicy: 'network-only' });
    reexecuteEgresos({ requestPolicy: 'network-only' });
  }, [refreshKey, reexecuteIngresos, reexecuteEgresos]);

  const entradasPage = ingresosData?.stockEntradas;
  const salidasPage = egresosData?.stockSalidas;
  const ingresos = entradasPage?.items ?? [];
  const egresos = salidasPage?.items ?? [];
  const loading = tab === 'ingresos' ? ingresosLoading : egresosLoading;
  const error = tab === 'ingresos' ? ingresosError : egresosError;
  const page = tab === 'ingresos' ? entradasPage : salidasPage;
  const rows = (tab === 'ingresos' ? ingresos : egresos).map(row => ({
    ...row,
    cliente: row.cliente || (row.clienteId ? `#${row.clienteId}` : '—'),
    usuario: row.usuario || (row.usuarioId ? `#${row.usuarioId}` : '—'),
  }));

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-6 py-8">
      <PageHeader title="Movimientos de stock" subtitle="Visualizá ingresos, egresos y transferencias registradas en el sistema."
        actions={<div className="flex flex-wrap gap-2">
          <button type="button" onClick={onCreateIngreso} className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-medium text-white">Registrar ingreso</button>
          <button type="button" onClick={onCreateEgreso} className="rounded-xl border border-[rgb(var(--brand))] px-4 py-2 text-sm font-medium">Registrar egreso</button>
          <button type="button" onClick={onTransferir} className="rounded-xl border border-[rgb(var(--brand))] px-4 py-2 text-sm font-medium">Transferir stock</button>
        </div>}
      />

      <section className="grid gap-3 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-4 md:grid-cols-4">
        <input aria-label="ID cliente" value={filters.clienteId} onChange={event => setFilters(current => ({ ...current, clienteId: event.target.value }))} placeholder="ID cliente" className="rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--bg))] px-3 py-2 text-sm" />
        <input aria-label="Artículo o remito" value={filters.search} onChange={event => setFilters(current => ({ ...current, search: event.target.value }))} placeholder="Artículo o remito" className="rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--bg))] px-3 py-2 text-sm" />
        <input aria-label="Desde" type="date" value={filters.fechaDesde} onChange={event => setFilters(current => ({ ...current, fechaDesde: event.target.value }))} className="rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--bg))] px-3 py-2 text-sm" />
        <input aria-label="Hasta" type="date" value={filters.fechaHasta} onChange={event => setFilters(current => ({ ...current, fechaHasta: event.target.value }))} className="rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--bg))] px-3 py-2 text-sm" />
      </section>

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-6 shadow-sm">
        <div className="mb-4 inline-flex rounded-full border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.6)] p-1">
          <button type="button" onClick={() => setTab('ingresos')} className={`rounded-full px-4 py-1 text-sm font-medium ${tab === 'ingresos' ? 'bg-[rgb(var(--brand))] text-white' : ''}`}>Ingresos</button>
          <button type="button" onClick={() => setTab('egresos')} className={`rounded-full px-4 py-1 text-sm font-medium ${tab === 'egresos' ? 'bg-[rgb(var(--brand))] text-white' : ''}`}>Egresos / Transferencias</button>
        </div>
        <div className="overflow-hidden rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.6)]">
          {loading ? <div className="px-4 py-6 text-sm text-muted">Cargando movimientos…</div>
            : error ? <div className="px-4 py-6 text-sm text-red-300">{error.message}</div>
              : <DataTable columns={columns} rows={rows} onRowClick={row => (tab === 'ingresos' ? onSelectIngreso : onSelectEgreso)?.(row.id)} pageSize={10} />}
        </div>
        {page ? <p className="mt-3 text-right text-xs text-muted">{page.total} movimientos · página {page.page} de {Math.max(1, Math.ceil(page.total / page.pageSize))}</p> : null}
      </section>

      <StickyActions
        left={<p className="text-sm text-muted">Todos los movimientos impactan en el Kardex y el stock valuado.</p>}
        right={<div className="flex flex-wrap gap-2">
          <button type="button" onClick={onCreateIngreso} className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-medium text-white">Ingreso</button>
          <button type="button" onClick={onCreateEgreso} className="rounded-xl border border-[rgb(var(--border))] px-4 py-2 text-sm font-medium">Egreso</button>
          <button type="button" onClick={onTransferir} className="rounded-xl border border-[rgb(var(--border))] px-4 py-2 text-sm font-medium">Transferencia</button>
        </div>}
      />
    </div>
  );
}
